import { Authority, User } from '@project44/ui-utils';
import { UserEnums } from '@project44-models/iam-gateway';

import { UIFormClient } from '../../../../../external/UIFormClient/domain/abstract/UIFormClient';
import { CheckboxFormProperty } from '../../../../../external/UIFormClient/infrastructure/ui/CheckboxFormProperty';
import { DropdownFormProperty } from '../../../../../external/UIFormClient/infrastructure/ui/DropdownFormProperty';
import { InputFieldFormProperty } from '../../../../../external/UIFormClient/infrastructure/ui/InputFieldFormProperty';
import { UserI18NPort } from '../../../infrastructure/ports/User.i18n.port';
import { TenantAdminUserContactPersonCheckbox } from '../../../infrastructure/ui/FormFields/TenantAdminUserContactPersonCheckbox/TenantAdminUserContactPerson.checkbox';
import { UserPasswordComponent } from '../../../infrastructure/ui/FormFields/UserPassword/UserPassword.component';
import { IUserCreationForm } from '../../interfaces';

export interface IUserCreationFormTemplate {
  properties: UIFormClient<IUserCreationForm>['properties'];
  transformUserCreationForm: (
    userCreationForm: IUserCreationForm,
  ) => IUserCreationForm;
}

export abstract class UserCreationFormTemplate {
  public abstract availableUserTypes: Array<UserEnums.UserTypeEnum> | undefined;
  public transformUserCreationForm(
    userCreationForm: IUserCreationForm,
  ): IUserCreationForm {
    return userCreationForm;
  }

  public compose(userPrincipal: User): IUserCreationFormTemplate {
    const canAssignTenantSpecificRoles = userPrincipal.hasAnyOfPermissions([
      <Authority>'system-admin:update:tenant-specific-user-role-assignments',
      <Authority>'SAVE_USER_ROLES',
    ]);

    const emptyInputField = {
      initialValue: '',
      isDisplayable: true,
      isEditable: true,
    };

    return {
      transformUserCreationForm: this.transformUserCreationForm,
      properties: {
        username: InputFieldFormProperty.create(
          { label: UserI18NPort.scopedTranslator('userCreationForm.username') },
          emptyInputField,
        ),
        givenName: InputFieldFormProperty.create(
          {
            label: UserI18NPort.scopedTranslator('userCreationForm.givenName'),
          },
          emptyInputField,
        ),
        familyName: InputFieldFormProperty.create(
          {
            label: UserI18NPort.scopedTranslator('userCreationForm.familyName'),
          },
          emptyInputField,
        ),
        password: UserPasswordComponent.create(
          {
            radioGroupProps: {
              value: 'DELEGATED',
            },
            radioButtons: [
              {
                value: 'DELEGATED',
                children: UserI18NPort.scopedTranslator(
                  'userCreationForm.delegatedPasswordUserCreation',
                ),
              },
              {
                value: 'setPassword',
                children: UserI18NPort.scopedTranslator(
                  'userCreationForm.requiredPasswordUserCreation',
                ),
              },
            ],
          },
          { isDisplayable: true, isEditable: true },
          {
            label: UserI18NPort.scopedTranslator('userCreationForm.password'),
            value: 'DELEGATED',
          },
        ),
        phoneNumber: InputFieldFormProperty.create(
          {
            label: UserI18NPort.scopedTranslator(
              'userCreationForm.phoneNumberOptional',
            ),
          },
          emptyInputField,
        ),
        userType:
          this.availableUserTypes === undefined
            ? TenantAdminUserContactPersonCheckbox.compose({
                initialValue: UserEnums.UserTypeEnum.USER,
                isDisplayable: true,
                isEditable: true,
                label: UserI18NPort.scopedTranslator(
                  'userCreationForm.tenantAdminUserContactPersonCheckboxLabel',
                ),
                helperText: UserI18NPort.scopedTranslator(
                  'userCreationForm.tenantAdminUserContactPersonCheckboxHelperText',
                ),
              })
            : DropdownFormProperty.compose(
                this.availableUserTypes.map((userTypeEnum) => {
                  return {
                    displayValue: this.translateUserTypeEnum(userTypeEnum),
                    value: userTypeEnum,
                  };
                }),
                {
                  initialValue: UserEnums.UserTypeEnum.USER,
                  isDisplayable: true,
                  isEditable: true,
                },
              ),
        isAdmin: CheckboxFormProperty.compose(
          {
            initialValue: 'false',
            isDisplayable: canAssignTenantSpecificRoles,
            isEditable: canAssignTenantSpecificRoles,
            label: UserI18NPort.scopedTranslator(
              'userCreationForm.isAdminCheckboxLabel',
            ),
            helperText: UserI18NPort.scopedTranslator(
              'userCreationForm.isAdminCheckboxHelperText',
            ),
          },
          { children: 'Administrator' },
        ),
      },
    };
  }

  private translateUserTypeEnum = (
    userType: UserEnums.UserTypeEnum,
  ): string => {
    const {
      USER,
      CONTACT_PERSON,
      CUSTOMER_INTEGRATION_USER,
      SYSTEM_USER,
      COMPANY_CREATOR,
      CLIENT_APPLICATION_USER,
      DATA_STREAM_USER,
      IAS,
    } = UserEnums.UserTypeEnum;

    const enumMap: Record<UserEnums.UserTypeEnum, string> = {
      [USER]: UserI18NPort.scopedTranslator('userTypeEnum.USER'),
      [CONTACT_PERSON]: UserI18NPort.scopedTranslator(
        'userTypeEnum.CONTACT_PERSON',
      ),
      [CUSTOMER_INTEGRATION_USER]: UserI18NPort.scopedTranslator(
        'userTypeEnum.CUSTOMER_INTEGRATION_USER',
      ),
      [SYSTEM_USER]: UserI18NPort.scopedTranslator('userTypeEnum.SYSTEM_USER'),
      [COMPANY_CREATOR]: UserI18NPort.scopedTranslator(
        'userTypeEnum.COMPANY_CREATOR',
      ),
      [CLIENT_APPLICATION_USER]: UserI18NPort.scopedTranslator(
        'userTypeEnum.CLIENT_APPLICATION_USER',
      ),
      [DATA_STREAM_USER]: UserI18NPort.scopedTranslator(
        'userTypeEnum.DATA_STREAM_USER',
      ),
      [IAS]: UserI18NPort.scopedTranslator('userTypeEnum.IAS'),
    };

    return enumMap[userType];
  };
}
